import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { errorDev } from '../utils/logger.js';

const PENDING_TRANSACTIONAL_ACTION_KEY = "orrith_pending_transactional_action";
const DEFAULT_AUTH_PROMPT = "Please log in or sign up to continue.";

export function setPendingTransactionalAction(action) {
  if (!action || typeof window === "undefined") return;
  try {
    const payload = {
      actionType: String(action.actionType || ""),
      payload: action.payload && typeof action.payload === "object" ? action.payload : {},
      redirectPath: String(action.redirectPath || "/"),
      createdAt: Date.now(),
    };
    window.sessionStorage.setItem(PENDING_TRANSACTIONAL_ACTION_KEY, JSON.stringify(payload));
  } catch (err) {
    // Ignore storage errors in restricted browser modes.
  errorDev("Operation failed:", err);
  }
}

export function getPendingTransactionalAction() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(PENDING_TRANSACTIONAL_ACTION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (!parsed.actionType || typeof parsed.actionType !== "string") return null;
    return parsed;
  } catch (err) {
    errorDev("Operation failed:", err);
    return null;
  }
}

export function clearPendingTransactionalAction() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(PENDING_TRANSACTIONAL_ACTION_KEY);
  } catch (err) {
    // Ignore storage errors in restricted browser modes.
  errorDev("Operation failed:", err);
  }
}

export function consumePendingTransactionalAction(predicate) {
  const pending = getPendingTransactionalAction();
  if (!pending) return null;

  if (typeof predicate === "function") {
    const accepted = predicate(pending);
    if (!accepted) return null;
  }

  clearPendingTransactionalAction();
  return pending;
}

export default function useTransactionalAuthGuard(currentUser) {
  const navigate = useNavigate();
  const location = useLocation();

  const requireAuthForTransactionalAction = useCallback(
    ({ actionType, payload = {}, message = DEFAULT_AUTH_PROMPT, messageKey = "auth.signInRequiredContinue" } = {}) => {
      if (currentUser) return true;

      const redirectPath = `${location.pathname}${location.search}`;
      setPendingTransactionalAction({
        actionType,
        payload,
        redirectPath,
      });

      navigate(`/login?redirect=${encodeURIComponent(redirectPath)}`, {
        replace: true,
        state: {
          authPromptKey: messageKey,
          authPrompt: message || DEFAULT_AUTH_PROMPT,
        },
      });

      return false;
    },
    [currentUser, location.pathname, location.search, navigate]
  );

  return { requireAuthForTransactionalAction };
}
